// Alice's proposal card — finished work awaiting sign-off (ProposedAction).
// Self-contained per-card Alpine component: hosts (email slideover, job
// slideover, approvals queue) only render the shared partial over their
// array; every behaviour lives here. Send-verb edits are delegated to the
// email composer via a window event (the composer is the editor; the
// executor is the only sender).

// Collapse bundle members (rows sharing a group_key) into their lead row so
// the partial renders ONE card per errand. The lead carries bundle_members
// (all members, group_position order). A row flagged _ungrouped (the
// "review steps individually" escape hatch) renders its members as normal
// single cards instead. Called from the partial's x-for, so it must be
// cheap and stable.
export function groupProposalBundles(rows) {
  const out = []
  const seen = new Set()
  for (const row of rows || []) {
    if (!row.group_key) { out.push(row); continue }
    if (seen.has(row.group_key)) continue
    seen.add(row.group_key)
    const members = rows
      .filter(r => r.group_key === row.group_key)
      .sort((a, b) => (a.group_position || 0) - (b.group_position || 0) || a.id - b.id)
    if (members.length < 2 || members.some(m => m._ungrouped)) {
      out.push(...members)
    } else {
      out.push({ ...members[0], bundle_members: members })
    }
  }
  return out
}

// The quiet mono footer both card variants end on — the same "Work item #N ·
// …" line the "Where we are" card closes with, so the two read as one app.
function proposalMetaLine(pa) {
  const parts = []
  if (pa.work_item_id) parts.push('Work item #' + pa.work_item_id)
  if (pa.generated_at) {
    const d = new Date(pa.generated_at)
    if (!isNaN(d)) {
      parts.push('Prepared ' +
        d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
        d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))
    }
  }
  return parts.join(' · ')
}

// The bundle card — one approval, many actions. Approve/dismiss hit the
// bundle endpoints; per-member statuses stream back and render as step
// ticks. "Review steps individually" ungroups client-side so each member
// becomes a normal card with its full edit surface.
export function proposedActionBundle(lead, opts = {}) {
  return {
    lead,
    members: lead.bundle_members,
    composerHost: !!opts.composer,
    busy: false,
    dismissing: false,
    dismissReason: '',
    error: null,
    expandedId: null,
    showEvidence: false,

    get openMembers() { return this.members.filter(m => m.status === 'proposed') },
    get isOpen() { return this.openMembers.length > 0 },
    get allExecuted() { return this.members.every(m => m.status === 'executed') },

    metaLine() { return proposalMetaLine(this.lead) },

    stepStatusIcon(m) {
      return {
        executed: 'ph-check-circle text-emerald-500',
        failed: 'ph-x-circle text-red-500',
        approved: 'ph-circle-notch text-violet-400',
        proposed: 'ph-circle text-[#D1D5DB] dark:text-gray-600'
      }[m.status] || 'ph-minus-circle text-gray-400'
    },

    ungroup() {
      this.members.forEach(m => { m._ungrouped = true })
      window.dispatchEvent(new CustomEvent('proposed-action:ungrouped', { detail: this.lead }))
    },

    approveAll() { this._decideBundle('approve_bundle', {}) },
    confirmDismissAll() { this._decideBundle('dismiss_bundle', { reason: this.dismissReason || null }) },

    async _decideBundle(action, payload) {
      if (this.busy) return
      this.busy = true
      this.error = null
      try {
        const resp = await fetch(`/proposed_actions/${action}`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ group_key: this.lead.group_key, ...payload })
        })
        const data = await resp.json().catch(() => ({}))
        if (Array.isArray(data.data)) {
          for (const updated of data.data) {
            const member = this.members.find(m => m.id === updated.id)
            if (member) Object.assign(member, updated)
          }
        }
        if (!resp.ok && !data.data) this.error = data.error || 'Something went wrong'
        if (!resp.ok && data.data) this.error = 'Not everything landed — see the steps below'
        if (resp.ok) {
          this.dismissing = false
          window.dispatchEvent(new CustomEvent('proposed-action:decided', { detail: this.lead }))
        }
      } catch (e) {
        this.error = 'Network error — try again'
      } finally {
        this.busy = false
      }
    }
  }
}

export function proposedActionCard(initial, opts = {}) {
  return {
    pa: initial,
    composerHost: !!opts.composer,
    signoffName: opts.signoffName || '',
    busy: false,
    editing: false,
    dismissing: false,
    dismissReason: '',
    editBuffer: {},
    // Send verbs edit inline on the card — the draft IS the editor. null
    // until touched, so an unread draft never submits a spurious "edit"
    // (contenteditable serialisation can differ byte-for-byte from the
    // original without any user change).
    draftSubject: null,
    draftBody: null,
    error: null,
    showEvidence: false,

    // The editor holds the WHOLE email, sign-off included: Alice's drafts
    // carry no sign-off (the approver's name is unknowable at draft time),
    // so the card seeds one in the exact form the executor would append.
    // A previously-edited draft (pa.signed_off) already contains its own —
    // seeding another would double it.
    seedBodyHtml() {
      const body = this.pa.preview.body_html || ''
      if (this.pa.signed_off) return body
      return body + '<p>Kind regards</p><p>' + this.signoffName + '</p>'
    },

    get isOpen() { return this.pa.status === 'proposed' },
    metaLine() { return proposalMetaLine(this.pa) },
    get isSendVerb() { return ['send_email_reply', 'send_new_email', 'propose_rent_review'].includes(this.pa.verb) },
    // Verbs whose content is generated deterministically by the product at
    // execute time (e.g. the quote-authorisation letter) have no edit surface.
    // Send verbs edit inline, so the Edit button only appears where a full
    // composer exists (the email slideover) as the cc/recipients escape hatch.
    get isEditable() {
      if (this.isDoorwayVerb) return false // edited in the workflow the card opens
      if (this.isSendVerb) return this.composerHost
      return !['send_quote_to_landlord', 'approve_quote', 'attach_document', 'create_job', 'record_supplier_invoice', 'record_quote', 'raise_charge', 'register_applicant', 'create_valuation'].includes(this.pa.verb)
    },

    // Bridge verbs: the card is a doorway into the real UI workflow,
    // pre-filled — it never executes behind the screen. The workflow's own
    // save carries proposed_action_id back and resolves the card, so the
    // record always goes through the real path, chains and all.
    // send_new_email is a doorway into the composer — except when the card
    // carries product-generated attachments, which only the executor can
    // stage at send time; those keep the inline approve path for now.
    get isDoorwayVerb() {
      if (this.pa.verb === 'send_new_email') return !(this.pa.preview?.attachments || []).length
      return ['create_job', 'record_supplier_invoice', 'record_quote', 'approve_quote', 'open_job', 'open_instruction', 'raise_charge', 'register_applicant', 'create_valuation'].includes(this.pa.verb)
    },

    // record_supplier_invoice is a pure doorway: the headline already states
    // the amount and both parties, and the charge form owns every editable
    // field. A card-face preview would only echo that read-only (and the
    // box otherwise renders as an empty "Net £0.00" shell), so we show none.
    get showsPreviewBody() { return this.pa.verb !== 'record_supplier_invoice' },

    doorwayLabel() {
      return {
        create_job: 'Open job form',
        record_supplier_invoice: 'Apply charge',
        record_quote: 'Open quote form',
        approve_quote: 'Approve quote',
        open_job: 'Open job',
        open_instruction: 'Open instruction',
        send_new_email: 'Open email',
        raise_charge: this.pa.payload?.charge_type === 'transfer' ? 'Open transfer form' : 'Open charge form',
        register_applicant: 'Open applicant form',
        create_valuation: 'Open valuation form'
      }[this.pa.verb] || 'Open'
    },

    // The issue a card is about, or the compliance renewal; a card from
    // before the cutover arrives with the issue its old job became
    issueTarget(p) {
      return p.renewal_id ? { renewal_id: p.renewal_id } : { id: p.issue_id }
    },

    openDoorway() {
      const p = this.pa.payload || {}

      if (this.pa.verb === 'create_job') {
        // The Add panel, pre-filled; its save carries the card's id back
        window.dispatchEvent(new CustomEvent('open-new-issue-slideover', {
          detail: {
            proposed_action_id: this.pa.id,
            property_id: p.property_id,
            title: p.title,
            description: p.description,
            priority: p.priority === 'urgent' ? 'urgent' : 'normal',
            reported_via: 'agent'
          }
        }))
      } else if (this.pa.verb === 'record_supplier_invoice') {
        // The issue slideover's invoice modal, filled in; its save carries the card's id back
        window.dispatchEvent(new CustomEvent('open-issue-slideover', {
          detail: {
            ...this.issueTarget(p),
            proposed_action_id: this.pa.id,
            invoice_prefill: {
              contractor_id: p.contractor_id,
              amount: p.net_amount_pence != null ? ((p.net_amount_pence + (p.vat_amount_pence || 0)) / 100).toFixed(2) : '',
              date: p.invoice_date || '',
              reference: p.invoice_reference || '',
              description: p.description || '',
              charge_mode: p.charge_mode || 'charge',
              email_attachment: p.email_attachment_id
                ? { id: p.email_attachment_id, filename: this.pa.preview?.document_label || 'the emailed invoice' }
                : null
            }
          }
        }))
      } else if (this.pa.verb === 'record_quote') {
        window.dispatchEvent(new CustomEvent('open-issue-slideover', {
          detail: {
            ...this.issueTarget(p),
            proposed_action_id: this.pa.id,
            quote_prefill: {
              contractor_id: p.contractor_id,
              contractor_name: this.pa.preview?.contractor_label,
              lines: p.lines,
              vat_rate: p.vat_rate,
              reference: p.quote_reference || '',
              issued_at: p.quote_issued_at || '',
              valid_until: p.quote_valid_until || '',
              notes: p.quote_notes || '',
              email_attachment: p.email_attachment_id
                ? { id: p.email_attachment_id, filename: this.pa.preview?.document_label || 'the emailed quote' }
                : null
            }
          }
        }))
      } else if (this.pa.verb === 'send_new_email') {
        // The composer is the product's own flow — open it pre-filled
        // (grounded recipients, subject, body + sign-off) and let the human
        // edit and send there; the compose send resolves the card.
        let recips
        if (Array.isArray(p.recipients) && p.recipients.length) {
          // Alice's shape: a grounded list of {type, id, to}.
          recips = p.recipients.map(r => ({ email: r.to, name: r.name || r.to, type: r.type, id: r.id }))
        } else if (p.to) {
          // AI Assistant's shape: a single grounded recipient — the bare
          // address lives in payload.to alongside the record it came from, so
          // use it directly and keep the chip linked to that person. The name
          // comes off the display line (preview.to is "Name <email>", card-face
          // only). recipient_type is a class name (Landlord) — the property
          // lookup endpoint wants it lower-cased.
          const m = String(this.pa.preview?.to || '').match(/^(.+?)\s*<\s*(.+?)\s*>$/)
          recips = [ {
            email: p.to,
            name: m ? m[1].trim() : p.to,
            type: p.recipient_type ? String(p.recipient_type).toLowerCase() : null,
            id: p.recipient_id || null
          } ]
        } else {
          // Last resort: no structured recipient at all — parse the display
          // line, pulling the bare address out of any "Name <email>" segment so
          // the chip's email isn't the whole string (which the send-time
          // validator rejects as invalid).
          recips = String(this.pa.preview?.to || '').split(',').map(e => e.trim()).filter(Boolean).map(part => {
            const m = part.match(/^(.+?)\s*<\s*(.+?)\s*>$/)
            return m ? { email: m[2], name: m[1].trim() || m[2] } : { email: part, name: part }
          })
        }
        window.dispatchEvent(new CustomEvent('open-email-compose', {
          detail: {
            proposed_action_id: this.pa.id,
            to: recips,
            subject: this.pa.preview?.subject || this.pa.headline,
            body_html: this.seedBodyHtml()
          }
        }))
      } else if (this.pa.verb === 'approve_quote') {
        // Lands the agent in the issue's own approval flow (the "please
        // proceed" email); its completion POST resolves this card.
        window.dispatchEvent(new CustomEvent('open-issue-slideover', {
          detail: {
            ...this.issueTarget(p),
            proposed_action_id: this.pa.id,
            approve_prefill: { contractor_id: p.contractor_id }
          }
        }))
      } else if (this.pa.verb === 'open_job') {
        // A persistent doorway (21 Jul): the next step lives on the issue
        // itself (change contractor, re-scope, chase) and the user decides
        // what to do there. Clicking only opens the door — the card stays
        // until dismissed, superseded, or the matter resolves.
        window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: this.issueTarget(p) }))
      } else if (this.pa.verb === 'open_instruction') {
        // The letting twin (25 Jul): pricing, the advert, photos, compliance,
        // viewings and go-live are all steps inside the instruction itself,
        // so the card opens it and the user takes whichever step is next.
        // Like open_job, clicking is not a decision — the card stays.
        window.dispatchEvent(new CustomEvent('open-instruction-slideover', { detail: { id: p.instruction_id } }))
      } else if (this.pa.verb === 'raise_charge') {
        // One form now, whatever the shape: a transfer to the landlord is the
        // charge composer with the landlord as payee.
        if (p.charge_type === 'transfer') {
          window.dispatchEvent(new CustomEvent('open-charge-form-slideover', {
            detail: {
              source: { type: 'tenancy', id: p.tenancy_id },
              proposed_action_id: this.pa.id,
              prefill: {
                net_amount_pence: p.net_amount_pence,
                description: p.description,
                date: p.date,
                who_paid: 'landlord'
              }
            }
          }))
        } else {
          window.dispatchEvent(new CustomEvent('open-charge-form-slideover', {
            detail: {
              source: { type: 'tenancy', id: p.tenancy_id },
              proposed_action_id: this.pa.id,
              prefill: {
                net_amount_pence: p.net_amount_pence,
                vat_amount_pence: p.vat_amount_pence,
                description: p.description,
                date: p.date,
                who_paid: 'agency'
              }
            }
          }))
        }
      } else if (this.pa.verb === 'register_applicant') {
        window.dispatchEvent(new CustomEvent('open-add-applicant-slideover', {
          detail: {
            proposed_action_id: this.pa.id,
            prefill: {
              first_name: p.first_name, last_name: p.last_name,
              email: p.email, phone: p.phone,
              min_beds: p.min_beds, max_beds: p.max_beds, max_rent: p.max_rent,
              furnished: p.furnished,
              needs_parking: p.needs_parking, needs_outside_space: p.needs_outside_space,
              area_ids: p.area_ids
            }
          }
        }))
      } else if (this.pa.verb === 'create_valuation') {
        // The new-business doorway: the Add-valuation form, pre-filled from
        // the correspondence. landlord_id/property_id are Alice's grounding —
        // they attach the lead to the landlord or address already on the book
        // instead of minting a twin, and the form passes them straight back.
        window.dispatchEvent(new CustomEvent('open-add-valuation-slideover', {
          detail: {
            proposed_action_id: this.pa.id,
            prefill: {
              first_name: p.first_name, last_name: p.last_name,
              email: p.email, phone: p.phone,
              house_number: p.house_number, street: p.street,
              town: p.town, postcode: p.postcode,
              property_type: p.property_type, beds: p.beds,
              status: p.status, valuation_at: p.valuation_at,
              notes: p.notes,
              landlord_id: p.landlord_id, property_id: p.property_id
            }
          }
        }))
      }
    },
    get sendsEmail() { return this.isSendVerb || ['send_quote_to_landlord', 'approve_quote'].includes(this.pa.verb) },

    // The inline draft differs from Alice's original — shown as a quiet
    // "edited" hint. The server independently diffs on approve, so a
    // type-then-undo false positive here never pollutes the trust ledger.
    get dirty() {
      if (!this.isSendVerb) return false
      const subjectChanged = this.draftSubject !== null &&
        this.draftSubject !== (this.pa.preview.subject || this.pa.headline)
      return this.draftBody !== null || subjectChanged
    },

    verbLabel() {
      return {
        send_email_reply: 'Reply',
        send_new_email: 'New email',
        add_note: 'Note',
        create_job: 'Job',
        update_job: 'Job update',
        add_certificate: 'Certificate',
        attach_document: 'File document',
        record_supplier_invoice: 'Invoice',
        record_quote: 'Quote',
        send_quote_to_landlord: 'To landlord',
        approve_quote: 'Approve quote',
        open_job: 'Go to job',
        open_instruction: 'Go to instruction',
        archive_thread: 'Archive thread',
        register_applicant: 'Register applicant',
        create_valuation: 'New valuation',
        set_rent_chasing: 'Rent chasing',
        propose_rent_review: 'Rent review',
        raise_charge: 'Charge'
      }[this.pa.verb] || this.pa.verb
    },

    verbIcon() {
      return {
        send_email_reply: 'ph-arrow-bend-up-left',
        send_new_email: 'ph-paper-plane-tilt',
        add_note: 'ph-note-pencil',
        create_job: 'ph-wrench',
        update_job: 'ph-arrows-clockwise',
        add_certificate: 'ph-seal-check',
        attach_document: 'ph-paperclip',
        record_supplier_invoice: 'ph-receipt',
        record_quote: 'ph-clipboard-text',
        send_quote_to_landlord: 'ph-envelope-simple',
        approve_quote: 'ph-check-circle',
        open_job: 'ph-arrow-square-out',
        open_instruction: 'ph-house-line',
        archive_thread: 'ph-archive',
        register_applicant: 'ph-user-plus',
        create_valuation: 'ph-house-line',
        set_rent_chasing: 'ph-bell-ringing',
        propose_rent_review: 'ph-trend-up',
        raise_charge: 'ph-currency-gbp'
      }[this.pa.verb] || 'ph-sparkle'
    },

    statusLabel() {
      return {
        executed: 'Done',
        approved: 'Approving…',
        failed: 'Failed',
        dismissed: 'Dismissed',
        superseded: 'Superseded',
        withdrawn: 'Withdrawn'
      }[this.pa.status] || this.pa.status
    },

    // Send verbs hand off to the composer (only reachable in composer
    // hosts — the cc/recipients escape hatch); note/job verbs edit in a
    // buffered mode. Both paths approve through the executor.
    startEdit() {
      if (this.isSendVerb) {
        window.dispatchEvent(new CustomEvent('proposed-action:edit-in-composer', { detail: this.pa }))
        return
      }
      this.editBuffer = JSON.parse(JSON.stringify(this.pa.preview || {}))
      this.editing = true
    },

    cancelEdit() {
      this.editing = false
      this.editBuffer = {}
    },

    // Approve submits the inline draft when touched; the server slices to
    // the verb's editable keys and records the diff (no-ops record nothing).
    // A touched body carries signed_off: it includes the sign-off (seeded or
    // rewritten by the user), so the executor must send it verbatim rather
    // than appending its own.
    approve() {
      const edits = {}
      if (this.isSendVerb) {
        if (this.draftBody !== null) {
          edits.body_html = this.draftBody
          edits.signed_off = true
        }
        if (this.draftSubject !== null && this.draftSubject !== (this.pa.preview.subject || this.pa.headline)) {
          edits.subject = this.draftSubject
        }
      }
      this._decide('approve', Object.keys(edits).length ? { edited_payload: edits } : {})
    },

    approveWithEdits() { this._decide('approve', { edited_payload: this.editBuffer }) },

    confirmDismiss() { this._decide('dismiss', { reason: this.dismissReason || null }) },

    async _decide(action, payload) {
      if (this.busy) return
      this.busy = true
      this.error = null
      try {
        const resp = await fetch(`/proposed_actions/${this.pa.id}/${action}`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        const data = await resp.json().catch(() => ({}))
        if (data.data) {
          Object.assign(this.pa, data.data)
        } else if (resp.status === 409) {
          this.pa.status = 'superseded'
          this.error = data.error
        } else if (!resp.ok) {
          this.error = data.error || 'Something went wrong'
        }
        if (resp.ok) {
          this.editing = false
          this.dismissing = false
          // Let the host refresh anything downstream (e.g. the thread's
          // message list after an executed send).
          window.dispatchEvent(new CustomEvent('proposed-action:decided', { detail: this.pa }))
        }
      } catch (e) {
        this.error = 'Network error — try again'
      } finally {
        this.busy = false
      }
    }
  }
}
